/**
 * @file 新闻管理页（News）
 *
 * 该文件定义了 CRM 系统的新闻管理组件 `CrmNewsPage`。
 * 主要功能包括：
 * - 新闻列表展示（表格形式，含标题、内容摘要、创建时间、更新时间）
 * - 按标题搜索（Enter 键触发）
 * - 新增/编辑新闻（模态框表单，含标题输入和富文本编辑器）
 * - 富文本编辑器集成图片上传功能（通过 uploadNewsImage 上传至后端）
 * - 删除新闻（带确认对话框，删除最后一项时自动回退到上一页）
 * - 分页导航
 *
 * 数据来源：`newsApi`（新闻 CRUD）、`uploadNewsImage`（图片上传）
 *
 * 设计决策：
 * - 使用 `<crm-rich-editor>` 富文本编辑器组件，支持自定义图片上传
 * - 图片上传后将 URL 拼接 token 参数返回给编辑器用于显示
 * - 搜索需按 Enter 键触发，避免输入时频繁请求
 * - 内容列表中使用 stripHtml 去除 HTML 标签后截断显示摘要
 * - 时间戳格式化为本地日期时间字符串
 * - 模态框背景点击关闭（mousedown + click 模式避免误触）
 */

import { css, html } from 'lit';
import { customElement, state } from 'lit/decorators.js';
import { newsApi, uploadNewsImage, API_BASE_URL } from '../api.js';
import { BaseElement } from '../base-element.js';
import type { News } from '../types.js';
import { toast } from '../components/toast.js';
import { showConfirm } from '../components/confirm-dialog.js';
import { isNetworkError } from '../utils/index.js';
import '../components/rich-editor.js';
import '../components/table-toolbar.js';
import '../components/loading-overlay.js';
import '../components/pagination.js';

/**
 * 去除 HTML 标签并截断为纯文本摘要
 *
 * 用于在列表中展示富文本内容的预览。
 * @param html - 原始 HTML 字符串
 * @param maxLen - 最大保留长度，默认 100
 * @returns 去除标签后的纯文本（超出长度时末尾添加「...」）
 */
function stripHtml(html: string, maxLen: number = 100): string {
  // 移除所有 HTML 标签和 &nbsp; 实体
  const text = html.replace(/<[^>]*>/g, '').replace(/&nbsp;/g, ' ').trim();
  return text.length > maxLen ? text.slice(0, maxLen) + '...' : text;
}

/**
 * 将 Unix 时间戳格式化为本地日期时间字符串
 * @param ts - Unix 时间戳（秒）
 * @returns 格式化的日期时间字符串，格式为 YYYY-MM-DD HH:mm
 */
function formatTimestamp(ts: number): string {
  const d = new Date(ts * 1000);
  const pad = (n: number) => n.toString().padStart(2, '0');
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())} ${pad(d.getHours())}:${pad(d.getMinutes())}`;
}

/**
 * 新闻管理页组件
 *
 * 继承 `BaseElement`，注册为 `<crm-news-page>` 自定义元素。
 * 提供新闻的增删改查功能，集成富文本编辑器和图片上传。
 *
 * 状态字段：
 * - `items` — 当前页新闻列表
 * - `isLoading` — 加载状态
 * - `total` / `currentPage` / `perPage` — 分页参数
 * - `search` — 搜索关键词
 * - `showModal` — 模态框开关
 * - `editingId` — 当前编辑的新闻 ID，0 表示新增模式
 * - `formTitle` / `formContent` — 表单标题和内容
 */
@customElement('crm-news-page')
export class CrmNewsPage extends BaseElement {
  /** 新闻列表数据 */
  @state() private items: News[] = [];
  /** 加载状态标志 */
  @state() private isLoading = false;
  /** 列表请求序号，仅允许最后一次搜索或分页请求更新页面。 */
  private _listRequestId = 0;
  /** 新闻总数，用于分页 */
  @state() private total = 0;
  /** 当前页码 */
  @state() private currentPage = 1;
  /** 每页条数 */
  @state() private perPage = 20;
  /** 搜索关键词，按标题搜索 */
  @state() private search = '';
  /** 是否显示新增/编辑模态框 */
  @state() private showModal = false;
  /** 当前编辑的新闻 ID，0 表示新增模式 */
  @state() private editingId = 0;
  /** 表单 - 新闻标题 */
  @state() private formTitle = '';
  /** 表单 - 新闻内容（富文本 HTML） */
  @state() private formContent = '';
  /** 表单 - NewsArticle JSON-LD 文本 */
  @state() private formJsonLd = '';

  /** 组件样式（空，依赖全局 Tailwind 样式） */
  static styles = css``;

  /**
   * 组件挂载到 DOM 后的生命周期回调
   *
   * 调用父类方法后加载新闻列表。
   */
  connectedCallback() {
    super.connectedCallback();
    this.loadNews();
  }

  /**
   * 加载新闻列表
   *
   * 根据当前分页和搜索关键词调用 `newsApi.list()` 获取数据。
   * 搜索关键词为空时不传递该参数。
   * 失败时显示错误 toast（过滤网络错误）。
   *
   * @returns 无返回值（Promise<void>）
   */
  private async loadNews() {
    const requestId = ++this._listRequestId;
    this.isLoading = true;
    try {
      const r = await newsApi.list({ page: this.currentPage, per_page: this.perPage, search: this.search || undefined });
      if (requestId !== this._listRequestId) return;
      this.items = r.data;
      this.total = r.pagination.total;
    } catch (err: any) {
      if (requestId !== this._listRequestId) return;
      const msg = err instanceof Error ? err.message : String(err);
      if (isNetworkError(msg)) return;
      toast.error(msg || '加载失败');
    } finally {
      if (requestId === this._listRequestId) this.isLoading = false;
    }
  }

  /**
   * 新闻表单提交处理器
   *
   * 校验标题非空后，根据编辑/新增模式分别调用 update/create API。
   * 成功后关闭模态框并重新加载列表。
   *
   * @param e - 表单提交事件
   * @returns 无返回值（Promise<void>）
   */
  private async handleSubmit(e: Event) {
    e.preventDefault();
    if (!this.formTitle.trim()) {
      toast.warning('请输入标题');
      return;
    }
    try {
      if (this.editingId) {
        await newsApi.update(this.editingId, {
          title: this.formTitle.trim(),
          content: this.formContent,
          json_ld: this.parseJsonLd(),
        });
        toast.success('更新成功');
      } else {
        await newsApi.create({ title: this.formTitle.trim(), content: this.formContent, json_ld: this.parseJsonLd() });
        toast.success('创建成功');
      }
      this.closeModal();
      await this.loadNews();
    } catch (err: any) {
      toast.error(err.message || '操作失败');
    }
  }

  /** 解析管理员填写的 JSON-LD，空值表示使用 Website 默认字段。 */
  private parseJsonLd(): Record<string, unknown> | null {
    const value = this.formJsonLd.trim();
    if (!value) return null;
    try {
      const parsed = JSON.parse(value);
      if (!parsed || typeof parsed !== 'object' || Array.isArray(parsed)) throw new Error('必须是 JSON 对象');
      return parsed;
    } catch (err) {
      throw new Error(`JSON-LD 格式错误：${err instanceof Error ? err.message : String(err)}`, { cause: err });
    }
  }

  /**
   * 打开新闻新增/编辑模态框
   *
   * 不传 ID 时为新增模式，传入 ID 时从当前列表查找对应新闻并预填表单。
   * @param id - 可选的新闻 ID，传入时为编辑模式
   */
  private openModal(id?: number) {
    this.editingId = id || 0;
    if (id) {
      // 编辑模式：从当前列表查找新闻并预填表单字段
      const item = this.items.find(i => i.id === id);
      if (item) {
        this.formTitle = item.title;
        this.formContent = item.content;
        this.formJsonLd = item.json_ld ? JSON.stringify(item.json_ld, null, 2) : '';
      }
    } else {
      // 新增模式：清空表单字段
      this.formTitle = '';
      this.formContent = '';
      this.formJsonLd = '';
    }
    this.showModal = true;
  }

  /**
   * 关闭新闻模态框并重置表单状态
   */
  private closeModal() {
    this.showModal = false;
    this.editingId = 0;
    this.formTitle = '';
    this.formContent = '';
    this.formJsonLd = '';
  }

  /**
   * 删除新闻
   *
   * 弹出确认对话框，用户确认后调用 `newsApi.delete()` 删除。
   * 如果当前页只剩一条数据且不在第一页，自动回退到上一页后重新加载。
   *
   * @param id - 待删除的新闻 ID
   * @returns 无返回值（Promise<void>）
   */
  private async handleDelete(id: number) {
    const ok = await showConfirm('确定要删除这条新闻吗？');
    if (!ok) return;
    try {
      await newsApi.delete(id);
      toast.success('删除成功');
      // 删除最后一项时自动回退到上一页，避免空白页
      if (this.items.length === 1 && this.currentPage > 1) {
        this.currentPage--;
      }
      await this.loadNews();
    } catch (err: any) {
      toast.error(err.message || '删除失败');
    }
  }

  /** 模态框背景点击检测标志，避免拖拽选中文本时误触发关闭 */
  private lastModalMousedown = false;

  /**
   * 渲染新闻管理页面
   *
   * 页面布局：
   * 1. 加载遮罩
   * 2. 顶部工具栏：搜索框（Enter 触发）、搜索按钮、新建新闻按钮、刷新/计数工具栏
   * 3. 新闻表格：标题（含内容摘要）、创建时间、更新时间、操作（编辑/删除）
   * 4. 分页组件
   * 5. 新闻新增/编辑模态框（条件渲染）：
   *    - 标题输入框
   *    - 富文本编辑器（支持自定义图片上传，上传后 URL 拼接 token 参数用于鉴权显示）
   *
   * 图片上传流程：
   * - 调用 `uploadNewsImage(file)` 上传至后端
   * - 图片读取接口为公开静态资源，直接使用后端返回 URL
   * - 将 displayUrl 传给富文本编辑器用于显示
   *
   * @returns Lit TemplateResult
   */
  render() {
    return html`
      <crm-loading-overlay ?active=${this.isLoading}></crm-loading-overlay>

      <div class="mb-2 flex items-center gap-3">
        <div class="flex-1 flex items-center gap-3">
          <input
            type="text" placeholder="搜索标题..."
            .value=${this.search}
            @input=${(e: Event) => { this.search = (e.target as HTMLInputElement).value; }}
            @keydown=${(e: KeyboardEvent) => { if (e.key === 'Enter') { this.currentPage = 1; this.loadNews(); } }}
            class="px-3 py-1.5 border border-border rounded-md text-sm bg-surface text-text focus:border-primary focus:ring-2 focus:ring-primary/20 focus:outline-none w-56"
          />
          <button class="px-3 py-1.5 text-sm font-medium text-text-secondary bg-bg hover:bg-border border border-border rounded-md transition-colors cursor-pointer"
                  @click=${() => { this.currentPage = 1; this.loadNews(); }}>
            搜索
          </button>
        </div>
        <button class="px-4 py-1.5 text-sm font-medium text-white bg-primary hover:bg-primary-hover rounded-md transition-colors cursor-pointer shadow-sm"
                @click=${() => this.openModal()}>
          + 新建新闻
        </button>
        <crm-table-toolbar
          .total=${this.total}
          .label=${'条新闻'}
          .loading=${this.isLoading}
          @crm-toolbar-refresh=${() => this.loadNews()}
        ></crm-table-toolbar>
      </div>

      <div class="bg-surface rounded-lg border border-border overflow-hidden">
        <div class="overflow-x-auto">
          <table class="w-full table-fixed">
            <thead>
              <tr class="border-b border-border bg-white text-left">
                <th class="px-4 py-2.5 text-xs font-semibold text-text-secondary uppercase tracking-wider">标题</th>
                <th class="px-4 py-2.5 text-xs font-semibold text-text-secondary uppercase tracking-wider w-40">创建时间</th>
                <th class="px-4 py-2.5 text-xs font-semibold text-text-secondary uppercase tracking-wider w-40">更新时间</th>
                <th class="px-4 py-2.5 text-center text-xs font-semibold text-text-secondary uppercase tracking-wider w-40">操作</th>
              </tr>
            </thead>
            <tbody>
              ${this.items.length === 0
                ? html`<tr><td colspan="4" class="px-4 py-12 text-center text-text-muted text-sm">暂无数据</td></tr>`
                : this.items.map(item => html`
                <tr class="border-b border-border hover:bg-bg/50 transition-colors">
                  <td class="px-4 py-3 min-w-0">
                    <button
                      type="button"
                      class="text-sm font-medium text-primary hover:text-primary-hover text-left truncate max-w-full cursor-pointer bg-transparent border-none p-0"
                      title="点击编辑新闻"
                      @click=${() => this.openModal(item.id)}
                    >${item.title}</button>
                    <div class="text-xs text-text-muted mt-0.5">${stripHtml(item.content, 100)}</div>
                  </td>
                  <td class="px-4 py-3 text-sm text-text-secondary">${formatTimestamp(item.created_at)}</td>
                  <td class="px-4 py-3 text-sm text-text-secondary">${formatTimestamp(item.updated_at)}</td>
                  <td class="px-4 py-3 w-40 whitespace-nowrap">
                    <div class="flex items-center justify-center gap-2">
                      <button class="px-2.5 py-1 text-xs font-medium text-primary hover:bg-primary/10 rounded transition-colors cursor-pointer"
                              @click=${() => this.openModal(item.id)}>编辑</button>
                      <button class="px-2.5 py-1 text-xs font-medium text-error hover:bg-error/10 rounded transition-colors cursor-pointer"
                              @click=${() => this.handleDelete(item.id)}>删除</button>
                    </div>
                  </td>
                </tr>
              `)}
            </tbody>
          </table>
        </div>
        <crm-pagination
          .current=${this.currentPage}
          .perPage=${this.perPage}
          .total=${this.total}
          @page-change=${(e: CustomEvent) => { this.currentPage = e.detail.page; this.loadNews(); }}
        ></crm-pagination>
      </div>

      ${this.showModal ? html`
        <div class="fixed inset-0 bg-black/40 backdrop-blur-sm flex justify-center items-start z-50 overflow-y-auto p-4"
             @mousedown=${(e: Event) => { this.lastModalMousedown = e.target === e.currentTarget; }}
             @click=${() => { if (this.lastModalMousedown) this.closeModal(); }}>
          <div class="bg-surface rounded-lg shadow-xl w-[80%] max-w-none border border-border my-8"
               @click=${(e: Event) => e.stopPropagation()}>
            <div class="flex justify-between items-center px-6 pt-5 pb-4 border-b border-border">
              <h2 class="text-lg font-semibold text-text">${this.editingId ? '编辑新闻' : '新建新闻'}</h2>
              <button type="button" class="p-1.5 rounded hover:bg-bg cursor-pointer text-text-muted transition-colors"
                      @click=${this.closeModal}>
                <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" class="w-5 h-5">
                  <path d="M18 6 6 18"/><path d="m6 6 12 12"/>
                </svg>
              </button>
            </div>
            <form @submit=${this.handleSubmit} class="p-6">
              <div class="mb-4">
                <label class="block mb-1.5 text-sm font-medium text-text">标题</label>
                <input type="text" .value=${this.formTitle}
                       @input=${(e: Event) => { this.formTitle = (e.target as HTMLInputElement).value; }}
                       class="w-full px-3.5 py-1.5 border border-border rounded-md text-sm bg-surface text-text focus:border-primary focus:ring-2 focus:ring-primary/20 focus:outline-none"
                       placeholder="输入新闻标题" required />
              </div>
              <div class="mb-5">
                <label class="block mb-1.5 text-sm font-medium text-text">内容</label>
                <crm-rich-editor
                  .value=${this.formContent}
                  .customImageUploader=${async (file: File) => {
                    const result = await uploadNewsImage(file);
                    return { cid: '', displayUrl: `${API_BASE_URL}${result.url}` };
                  }}
                  placeholder="输入新闻内容..."
                  @input=${(e: CustomEvent) => { this.formContent = e.detail.value; }}
                  style="min-height: 300px;"
                ></crm-rich-editor>
              </div>
              <div class="mb-5">
                <label class="block mb-1.5 text-sm font-medium text-text">NewsArticle JSON-LD（可选）</label>
                <textarea
                  .value=${this.formJsonLd}
                  @input=${(e: Event) => { this.formJsonLd = (e.target as HTMLTextAreaElement).value; }}
                  class="w-full min-h-40 px-3 py-2 border border-border rounded-md text-sm font-mono bg-surface text-text focus:border-primary focus:ring-2 focus:ring-primary/20 focus:outline-none"
                  placeholder='{"@type":"NewsArticle","author":{"@type":"Person","name":"作者姓名"}}'
                ></textarea>
              </div>
              <div class="flex justify-end gap-2">
                <button type="button"
                        class="px-4 py-1.5 text-sm font-medium text-text-secondary bg-bg hover:bg-border border border-border rounded-md transition-colors cursor-pointer"
                        @click=${this.closeModal}>取消</button>
                <button type="submit"
                        class="px-4 py-1.5 text-sm font-medium text-white bg-primary hover:bg-primary-hover rounded-md transition-colors cursor-pointer shadow-sm">保存</button>
              </div>
            </form>
          </div>
        </div>
      ` : ''}
    `;
  }
}
